import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { APIService } from 'src/app/global/services/api.service';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { environment } from 'src/environments/environment';
import { catchError, shareReplay, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
/** *Dashboard Service */
export class DashboardService {
	API_URL: string = environment.apiUrl;
	ACCOUNT_API_V2: string = environment.ACCOUNT_API_V2;
	DONATION_URL: string = environment.DONATION_API_V2;
	PROJECT_URL: string = environment.FUNDRAISER_API_V2;
	NOTIF_URL: string = environment.fundraiser_features_url;
	FUNDRAISER_URL: string = environment.fundraiser_url;

	private readonly stopRecurringDonationAPI = 'donation/stripe/customer/portal';
	private readonly profileAPI = 'account/profile/';
	private readonly transactionBalanceAPI = 'accounting/transaction/balance/';
	private readonly userFundraiserAPI =
		'fundraiser/get_user_fundraisers_status/';
	private readonly donationReceivedAPI = 'donation/order/receiver/';
	private readonly donationGivenAPI = 'donation/order/donor/';
	private readonly donationRecurringReceivedAPI = 'donation/recurring/received';
	private readonly donationRecurringGivenAPI = 'donation/recurring/given';
	private readonly cancelRecurringAPI = 'donation/order/cancel_periodic';
	private readonly fundraiserSummary = 'fundraiser/summary';
	private readonly donationSummary = 'donation/summary';
	private readonly myFundraisers = 'fundraiser/all/fundraiser/';
	private readonly fundraisersList = 'qr/fundraiser/list';
	private readonly downloadCsv = 'donation/download';
	private readonly totalDonationSummary = 'donation/order/total';
	private objectSource = new BehaviorSubject(Object);
	currentObject = this.objectSource.asObservable();
	private header!: HttpHeaders;
	private fundraiserSummary$: Observable<any> | null = null;
	private readonly CACHE_DURATION = 10000;
	private fundraiserSummaryCacheTimeout: any;
	constructor(
		public _accountService: AccountService,
		private _apiService: APIService,
		private http: HttpClient
	) {
		this.header = this._accountService.getHeaders() as HttpHeaders;
	}

	changeObj(data: any) {
		this.objectSource.next(data);
	}
	/** *Profile data */
	// getProfile() {
	// 	this.header = this._accountService.getHeaders() as HttpHeaders;
	// 	return this.http.get(environment.ACCOUNT_API_V2 + this.profileAPI, {
	// 		headers: this.header,
	// 	});
	// }

	/** *Transaction Balance Amount */
	getTransactionBalance() {
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this._apiService.get(this.transactionBalanceAPI, {
			headers: this.header,
		});
	}

	/** *My fundraiser Data */
	getMyfundraisersAll(params: any) {
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(`${this.FUNDRAISER_URL}${this.myFundraisers}`, {
			headers: this.header,
			params,
		});
	}

	/* Fundraisers list of a user */
	getFundraisersList(){
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(`${this.FUNDRAISER_URL}${this.fundraisersList}`, {
			headers: this.header
		});
	}

	/** *Logic for Profile card data */
	filterProfileCardData(profileResponse: any, balanceResponse: any) {
		let default_logo =
			'https://imagedelivery.net/_0vgnXOEIHPwLg2E52a7gg/production/profile_image/default/public';
		let obj = {
			image: profileResponse?.profile?.image || default_logo,
			name: profileResponse?.profile?.name || 'Hello, User',
			balance: parseFloat(balanceResponse).toFixed(2) || 0,
		};
		return obj;
	}

	/** *Logic for creating Steps To Do data */
	filterStepsToDoData(
		profileResponse: any,
		myFundraisersResponse: any,
		donationGivenResponse: any
	): any[] {
		return [
			{
				name: $localize`:@@dashboard_createAccount_step:Create account`,
				status: true,
				showStep: true,
			},
			{
				name: $localize`:@@dashboard_startFundraiser_step:Start fundraiser`,
				status: myFundraisersResponse?.count > 0 ? true : false,
				showStep: true,
			},
			{
				name: $localize`:@@dashboard_bankDetails_step:Add bank details`,
				status: profileResponse?.has_bank_information ? true : false,
				showStep: profileResponse?.profile?.is_receiver ? true : false,
				// showStep: true, // should allow to show it for newly registered users
			},
			{
				name: $localize`:@@dashboard_customizeBranding_step:Customize branding`,
				status: profileResponse?.profile?.is_default ? false : true,
				showStep: profileResponse?.profile?.is_receiver ? true : false,
				// showStep: true,
			},
			{
				name: $localize`:@@dashboard_makeADonation_step:Make a donation`,
				status: donationGivenResponse?.count > 0 ? true : false,
				showStep: myFundraisersResponse?.count > 0 ? true : false,
			},
		];
	}

	// get total amount, count and average of given, received, recurring_given and recurring_received donation
	getTotalDonationSummary(dataType: string) {
		const params = new HttpParams({
			fromObject: {
				['data']: dataType,
			},
		});
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(
			environment.DONATION_API_V2 + this.totalDonationSummary,
			{
				headers: this.header,
				params,
			}
		);
	}

	// Download csv for received, given, recurring_received and recurring_given donation
	getDonationDownloadCsvData(
		fromDate: number,
		toDate: number,
		languageCode: string,
		filter: string,
		dataType: string
	) {
		const params = new HttpParams({
			fromObject: {
				['from_date']: fromDate,
				['to_date']: toDate,
				['language_code']: languageCode || 'en',
				['filter']: filter || '',
				['data']: dataType,
			},
		});

		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(environment.DONATION_API_V2 + this.downloadCsv, {
			headers: this.header,
			params,
		});
	}

	/** *Donation Received table */
	/** *Donation Received table */
	getDonationReceived(
		params = new HttpParams({
			fromObject: {
				['filter']: '',
				['sort_col']: 'created_at',
				['sort_direction']: 'asc',
				['page']: 1,
				['page_size']: 20,
				['from_date']: 0,
				['to_date']: new Date().getTime(),
				['currency']: 'eur',
			},
		})
	) {
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(
			environment.DONATION_API_V2 + this.donationReceivedAPI,
			{
				headers: this.header,
				params,
			}
		);
	}
	getDonationReceivedObj(receivedDonationResponse: any) {
		return {
			count: receivedDonationResponse?.data?.count || 0,
			next: receivedDonationResponse?.data?.next,
			donationReceived: this.filterDonationReceivedData(
				receivedDonationResponse?.data
			),
		};
	}
	filterDonationReceivedData(receivedDonationResponse: any) {
		return receivedDonationResponse.results;
	}

	/** *Donation Given table */
	/** *Donation Given table */
	getDonationGiven(
		params = new HttpParams({
			fromObject: {
				['filter']: '',
				['sort_col']: 'created_at',
				['sort_direction']: 'asc',
				['page']: 1,
				['page_size']: 20,
				['from_date']: 0,
				['to_date']: new Date().getTime(),
				['currency']: 'eur',
			},
		})
	) {
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(environment.DONATION_API_V2 + this.donationGivenAPI, {
			headers: this.header,
			params,
		});
	}
	getDonationGivenObj(givenDonationResponse: any) {
		return {
			count: givenDonationResponse?.data?.count || 0,
			next: givenDonationResponse?.data?.next,
			donationGiven: this.filterDonationGivenData(givenDonationResponse?.data),
		};
	}
	filterDonationGivenData(givenDonationResponse: any) {
		return givenDonationResponse.results;
	}

	/** *Recurring Received Donation table */
	getRecurringReceivedDonation(
		params = new HttpParams({
			fromObject: {
				['language_code']: 'en',
				['page']: 1,
				['limit']: 20,
				['from_date']: 0,
				['to_date']: Math.floor(Date.now() / 1000),
				['filter']: '',
				['currency']: 'eur',
			},
		})
	) {
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.get(
			this.DONATION_URL + this.donationRecurringReceivedAPI,
			{
				headers: this.header,
				params,
			}
		);
	}
	getRecurringReceivedDonationObj(RecurringReceivedDonationResponse: any) {
		return {
			count: RecurringReceivedDonationResponse?.data?.result?.total || 0,
			next: RecurringReceivedDonationResponse?.next,
			donationRecurringReceived: this.filterRecurringReceivedDonationData(
				RecurringReceivedDonationResponse
			),
		};
	}
	filterRecurringReceivedDonationData(RecurringReceivedDonationResponse: any) {
		return RecurringReceivedDonationResponse.data?.result?.result;
	}

	/** *Recurring Given Donation table */
	getRecurringGivenDonation(
		params = new HttpParams({
			fromObject: {
				['language_code']: 'en',
				['page']: 1,
				['limit']: 20,
				['from_date']: 0,
				['to_date']: Math.floor(Date.now() / 1000),
				['filter']: '',
				['currency']: 'eur',
			},
		})
	) {
		this.header = this._accountService.getHeaders() as HttpHeaders;

		return this.http.get(this.DONATION_URL + this.donationRecurringGivenAPI, {
			headers: this.header,
			params,
		});
	}
	getRecurringGivenDonationObj(RecurringGivenDonationResponse: any) {
		return {
			count: RecurringGivenDonationResponse?.data?.result?.total || 0,
			next: RecurringGivenDonationResponse?.next,
			donationRecurringGiven: this.filterRecurringGivenDonationData(
				RecurringGivenDonationResponse
			),
		};
	}
	filterRecurringGivenDonationData(RecurringGivenDonationResponse: any) {
		return RecurringGivenDonationResponse.data.result?.result;
	}
	/** *Action from Recurring Given Donation table */
	cancelRecurringDonation(idParam: number) {
		let body = {
			id: idParam,
		};
		this.header = this._accountService.getHeaders() as HttpHeaders;
		return this.http.post(this.API_URL + this.cancelRecurringAPI, body, {
			headers: this.header,
		});
	}
	// this api is used to stop recurring donation
	stopRecurringDonationRecieved(subscription_id: string) {
		const return_url = window.location.href;
		let header = this._accountService.getHeaders() as HttpHeaders;
		const body = {
			subscription_id: subscription_id,
			return_url: return_url,
		};
		return this.http.post(
			environment.DONATION_API_V2 + this.stopRecurringDonationAPI,
			body,
			{ headers: header }
		);
	}

	/** This api is used to get fundraiser summary*/
	// getFundraiserSummary() {
	// 	let options = {
	// 		headers: this._accountService.getHeaders(),
	// 	};
	// 	return this.http.get(this.PROJECT_URL + this.fundraiserSummary, options);
	// }
	// when expire?
	getFundraiserSummary(): Observable<any> {
		if (!this.fundraiserSummary$) {
			const options = {
				headers: this._accountService.getHeaders(),
			};

			this.fundraiserSummary$ = this.http
				.get(this.PROJECT_URL + this.fundraiserSummary, options)
				.pipe(
					shareReplay(1) // Cache the response
				);
			// Set cache timeout
			this.setCacheTimeout('fundraiserSummary');
		}
		return this.fundraiserSummary$;
	}
	private setCacheTimeout(cacheType: 'fundraiserSummary') {
		if (cacheType === 'fundraiserSummary') {
			if (this.fundraiserSummaryCacheTimeout) {
				clearTimeout(this.fundraiserSummaryCacheTimeout);
			}
			this.fundraiserSummaryCacheTimeout = setTimeout(() => {
				this.fundraiserSummary$ = null;
			}, this.CACHE_DURATION);
		}
	}
	clearCache() {
		this.fundraiserSummary$ = null;
		if (this.fundraiserSummaryCacheTimeout) {
			clearTimeout(this.fundraiserSummaryCacheTimeout);
			this.fundraiserSummaryCacheTimeout = null;
		}
	}

	/** This api is used to get donation summary*/
	getDonationSummary() {
		let options = {
			headers: this._accountService.getHeaders(),
		};
		return this.http.get(this.DONATION_URL + this.donationSummary, options);
	}

	downloadCSV(transactions: any, value: any) {
		const encodedUri = encodeURI(transactions);
		const link = document.createElement('a');
		link.setAttribute('href', encodedUri);
		link.setAttribute('download', `${value}.csv`);
		link.innerHTML = 'Click Here to download';
		document.body.appendChild(link); // Required for FF
		link.click();
	}
}
